/**
 * MultiFileUpload - Shared component for uploading multiple files
 *
 * Features:
 * - Drag and drop support
 * - Multiple file selection
 * - File validation (type, size)
 * - Visual feedback for selected files
 * - Remove individual files
 *
 * Used by: Upload page (document analysis), KnowledgeBase page
 */

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloudUpload, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface FileValidationOptions {
  allowedTypes: string[];
  maxFileSize: number;
  accept?: string; // HTML accept attribute (e.g., ".pdf,.docx")
}

interface MultiFileUploadProps {
  onFilesSelect: (files: File[]) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
  validationOptions: FileValidationOptions;
  /**
   * Optional per-file validator. When supplied, it overrides the default
   * type/size check derived from {@link validationOptions}. Useful when
   * different file kinds (e.g. documents vs images) have different size
   * caps that a single threshold cannot express. Return `null` for ok or
   * a localized error message string.
   */
  validateFile?: (file: File) => string | null;
  translationNamespace?: string; // Default: "upload"
  showSelectedFiles?: boolean; // Default: true
  className?: string;
}

export function MultiFileUpload({
  onFilesSelect,
  onError,
  disabled = false,
  validationOptions,
  validateFile,
  translationNamespace = "upload",
  showSelectedFiles = true,
  className = "",
}: MultiFileUploadProps) {
  const { t } = useTranslation([translationNamespace]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultValidateFile = (file: File): string | null => {
    const normalizedType = file.type.toLowerCase();
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    // Check type
    const isAllowedType = validationOptions.allowedTypes.some((type) =>
      normalizedType === type
    );

    // Special handling for markdown files (may have empty MIME type)
    const isMarkdown = fileExtension === "md";
    const isMarkdownAllowed = validationOptions.allowedTypes.includes(
      "text/markdown",
    );

    if (!isAllowedType) {
      if (
        !(isMarkdown && isMarkdownAllowed &&
          (normalizedType === "" || normalizedType === "text/plain"))
      ) {
        return t("fileUpload.errors.invalidType");
      }
    }

    // Check size
    if (file.size > validationOptions.maxFileSize) {
      const maxSizeMB = Math.floor(validationOptions.maxFileSize / 1024 / 1024);
      return t("fileUpload.errors.tooLarge", {
        maxSize: maxSizeMB,
      });
    }

    return null;
  };

  const validateFileFn = validateFile ?? defaultValidateFile;

  const handleFiles = (files: FileList, source?: HTMLInputElement) => {
    const fileArray = Array.from(files);
    // An empty pick (cleared input) is a no-op, not a selection: it must not
    // echo onFilesSelect([]) or the consumer would wrongly clear a prior valid
    // selection just because the input's value was reset.
    if (fileArray.length === 0) {
      if (source) {
        source.value = "";
      }
      return;
    }
    const errors: string[] = [];
    const validFiles: File[] = [];

    for (const file of fileArray) {
      const error = validateFileFn(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    }

    // Single source of truth: always echo the surviving selection (even an
    // empty one for a fully-invalid pick) so the consumer's `selectedFiles`
    // mirrors exactly what can be submitted. This must run BEFORE onError:
    // the consumer's select handler clears its error state as a "fresh
    // selection" side effect, so a rejection emitted first would be clobbered
    // later in the same batched React event and the dropped files would be
    // invisible. The consumer's error handler therefore no longer needs to
    // clear its own selection - the selection echo owns it.
    setSelectedFiles(validFiles);
    onFilesSelect(validFiles);

    if (errors.length > 0) {
      onError?.(errors.join("\n"));
    }

    // Reset the native input so picking the same file again fires a new
    // change event (browsers fire none for an unchanged value).
    if (source) {
      source.value = "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files, e.target);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFilesSelect(newFiles);
  };

  return (
    <div className={`w-full ${className}`}>
      <input
        ref={inputRef}
        type="file"
        accept={validationOptions.accept}
        onChange={handleChange}
        disabled={disabled}
        multiple
        className="hidden"
        aria-label={t("fileUpload.ariaLabel")}
        data-test="file-input"
      />

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t("fileUpload.ariaLabel")}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-test="file-dropzone"
        className={`
          relative cursor-pointer rounded-[var(--r-lg)] border-2 border-dashed p-8 text-center
          transition-all duration-[var(--dur-base)]
          ${
          dragActive
            ? "border-[var(--primary)] bg-[var(--accent)] shadow-[0_0_0_1px_color-mix(in_srgb,var(--brand)_20%,transparent)]"
            : "border-[var(--border)] bg-[color:color-mix(in_srgb,var(--card)_84%,transparent)] hover:border-[var(--primary)] hover:bg-[var(--accent)]/60"
        }
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
      >
        <div className="flex flex-col items-center space-y-3">
          <CloudUpload className="h-12 w-12 text-[var(--muted-foreground)]" aria-hidden="true" />

          <div>
            <p className="text-sm font-medium text-[var(--foreground)]">
              {selectedFiles.length > 0
                ? (
                  <span className="text-[var(--primary)]">
                    {t("fileUpload.filesSelected", {
                      count: selectedFiles.length,
                    })}
                  </span>
                )
                : (
                  <>
                    <span className="text-[var(--primary)]">
                      {t("fileUpload.clickToUpload")}
                    </span>{" "}
                    {t("fileUpload.orDragAndDrop")}
                  </>
                )}
            </p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              {t("fileUpload.fileTypeMultiple")}
            </p>
          </div>
        </div>
      </div>

      {showSelectedFiles && selectedFiles.length > 0 && (
        <div className="mt-4 w-full text-left">
          <p className="mb-2 text-xs font-medium text-[var(--muted-foreground)]">
            {t("fileUpload.selectedFilesLabel")}
          </p>
          <ul className="space-y-1">
            {selectedFiles.map((file, index) => (
              <li
                key={index}
                className="group flex items-center justify-between rounded-[var(--r-sm)] border border-transparent px-2 py-2 text-xs text-[var(--foreground)] transition hover:border-[var(--border)] hover:bg-[var(--accent)]"
              >
                <span className="truncate flex-1">• {file.name}</span>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(index);
                  }}
                  className="ml-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 text-[var(--muted-foreground)] hover:text-[var(--destructive)]"
                  data-test={`remove-file-${index}`}
                  aria-label={t("fileUpload.removeFile", {
                    filename: file.name,
                  })}
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
